// src/client/components/settings/SelectorsTab.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Label } from '@client/components/ui/label';
import { Code, Plus, Power, PowerOff } from 'lucide-react';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys } from '@client/lib/query-keys';
import { useCrudMutations } from '@client/hooks/useCrudMutations';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import type { Selector, SelectorFormData } from '@client/types/settings';
import { validateSelectorForm } from '@client/types/settings';
import { defaultFeatureConfig } from '@shared/defaults/';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@client/components/ui/select';

export default function SelectorsTab() {
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingSelector, setEditingSelector] = useState<Selector | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [formData, setFormData] = useState<SelectorFormData>({
    name: '',
    category: '',
    selector: '',
    description: '',
    isActive: true,
  });
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch selectors
  const { data: selectors = [], isLoading } = useQuery({
    queryKey: queryKeys.selectors.lists(),
    queryFn: async () => {
      const data = await ApiClient.get('/selectors');
      // Handle array or wrapped response
      if (Array.isArray(data)) return data as Selector[];
      //if (data && Array.isArray(data.selectors)) return data.selectors as Selector[];
      //if (data && Array.isArray(data.data)) return data.data as Selector[];
      return [] as Selector[];
    },
  });

  // CRUD Mutations
  const { createMutation, updateMutation, deleteMutation, toggleActiveMutation } = useCrudMutations<Selector>({
    endpoint: '/selectors',
    queryKey: queryKeys.selectors.lists() as any,
    entityName: 'Selector',
  });

  // Helper Functions
  const getFeatureByValue = (value: string) => {
    return defaultFeatureConfig.find((feature) => feature.value === value);
  };

  const getCategoryBadgeColor = (category: string) => {
    const feature = getFeatureByValue(category);
    return feature?.class || 'bg-gray-100 text-gray-800';
  };

  const getCategoryLabel = (category: string) => {
    const feature = getFeatureByValue(category);
    return feature?.label || category;
  };

  const availableCategories = Array.from(
    new Set([...selectors.map((s) => s.category), ...defaultFeatureConfig.map((f) => f.value)])
  );

  const filteredSelectors =
    selectedCategory === 'all' ? selectors : selectors.filter((s) => s.category === selectedCategory);

  // Dialog Handlers
  const openCreateDialog = () => {
    setFormData({ name: '', category: '', selector: '', description: '', isActive: true });
    setFormError(null);
    setEditingSelector(null);
    setDialogMode('create');
  };

  const openEditDialog = (selector: Selector) => {
    setEditingSelector(selector);
    setFormData({
      name: selector.name,
      category: selector.category,
      selector: selector.selector,
      description: selector.description || '',
      isActive: selector.isActive,
    });
    setFormError(null);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingSelector(null);
    setFormData({ name: '', category: '', selector: '', description: '', isActive: true });
    setFormError(null);
  };

  const handleSave = () => {
    const error = validateSelectorForm(formData);
    if (error) {
      setFormError(error);
      return;
    }

    if (dialogMode === 'edit' && editingSelector) {
      updateMutation.mutate({ id: editingSelector.id, data: formData }, { onSuccess: closeDialog });
    } else {
      createMutation.mutate(formData, { onSuccess: closeDialog });
    }
  };

  const handleDelete = (selector: Selector) => {
    deleteMutation.mutate(selector.id);
  };

  const handleToggleActive = (selector: Selector) => {
    toggleActiveMutation.mutate({ id: selector.id, isActive: !selector.isActive });
  };

  // Table Columns
  const columns: TableColumn<Selector>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (selector) => <span className="font-medium">{selector.name}</span>,
    },
    {
      key: 'category',
      header: 'Feature-Modul',
      render: (selector) => (
        <Badge className={getCategoryBadgeColor(selector.category)}>
          {getCategoryLabel(selector.category)}
        </Badge>
      ),
    },
    {
      key: 'selector',
      header: 'Selector',
      className: 'font-mono text-sm max-w-xs',
      render: (selector) => (
        <div className="truncate" title={selector.selector}>
          {selector.selector}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (selector) => (
        <Badge variant={selector.isActive ? 'default' : 'secondary'}>
          {selector.isActive ? 'Aktiv' : 'Inaktiv'}
        </Badge>
      ),
    },
    {
      key: 'description',
      header: 'Beschreibung',
      className: 'max-w-xs',
      render: (selector) => (
        <div className="truncate" title={selector.description || ''}>
          {selector.description || '-'}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Letzte Änderung',
      className: 'text-sm text-muted-foreground',
      render: (selector) => new Date(selector.updatedAt).toLocaleDateString('de-DE'),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              CSS Selectors
            </CardTitle>
            <CardDescription>
              CSS-Selektoren für die LinkedIn-Automatisierung im Userscript verwalten.
            </CardDescription>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Neuer Selector
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filter */}
        <div className="flex items-center space-x-4">
          <Label htmlFor="category-filter">Feature-Modul filtern:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger id="category-filter" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Module ({selectors.length})</SelectItem>
              {availableCategories.map((category) => {
                const feature = getFeatureByValue(category);
                const count = selectors.filter((s) => s.category === category).length;
                return (
                  <SelectItem key={category} value={category}>
                    {feature?.label || category} ({count})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <DataTable
          data={filteredSelectors}
          columns={columns}
          loading={isLoading}
          getRowKey={(selector) => selector.id}
          emptyState={{
            icon: <Code className="h-12 w-12 opacity-50" />,
            title: 'Keine Selectors gefunden',
            description: 'Erstellen Sie einen neuen Selector, um zu beginnen.',
          }}
          actions={{
            onEdit: openEditDialog,
            onDelete: handleDelete,
            custom: [
              {
                icon: <PowerOff className="h-4 w-4 text-orange-600" />,
                label: 'Status ändern',
                onClick: handleToggleActive,
                variant: 'ghost',
              },
            ],
          }}
          deleteConfirmation={{
            title: 'Selector löschen?',
            description: (selector) =>
              `Sind Sie sicher, dass Sie den Selector "${selector.name}" löschen möchten? Diese Aktion könnte die Funktionalität des ${getCategoryLabel(selector.category)} Features beeinträchtigen.`,
          }}
        />
      </CardContent>

      {/* CRUD Dialog */}
      <CrudDialog
        open={dialogMode !== null}
        onOpenChange={(open) => !open && closeDialog()}
        mode={dialogMode || 'create'}
        title={dialogMode === 'edit' ? 'Selector bearbeiten' : 'Neuer Selector'}
        description={
          dialogMode === 'edit'
            ? `Ändern Sie die Eigenschaften des Selectors "${editingSelector?.name}".`
            : 'Erstellen Sie einen neuen CSS-Selector für ein spezifisches Feature-Modul.'
        }
        onSave={handleSave}
        onCancel={closeDialog}
        isSaving={createMutation.isPending || updateMutation.isPending}
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            id="name"
            label="Name"
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: String(value) })}
            placeholder="z.B. PROFILE_NAME"
            required
          />

          <FormField
            id="category"
            label="Feature-Modul"
            type="select"
            value={formData.category}
            onChange={(value) => setFormData({ ...formData, category: String(value) })}
            options={defaultFeatureConfig.map((feature) => ({
              value: feature.value,
              label: feature.label,
              icon: <div className={`w-3 h-3 rounded-full ${feature.class.split(' ')[0]}`} />,
            }))}
            required
          />
        </div>

        <FormField
          id="selector"
          label="CSS Selector"
          value={formData.selector}
          onChange={(value) => setFormData({ ...formData, selector: String(value) })}
          placeholder="z.B. .entity-result__title-text"
          required
          className="font-mono"
        />

        <FormField
          id="description"
          label="Beschreibung"
          type="textarea"
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: String(value) })}
          placeholder="Beschreibung des Selectors..."
          rows={3}
        />

        <FormField
          id="isActive"
          label="Aktiv"
          type="switch"
          value={formData.isActive}
          onChange={(value) => setFormData({ ...formData, isActive: Boolean(value) })}
        />

        {formError && <p className="text-sm text-red-500">{formError}</p>}
      </CrudDialog>
    </Card>
  );
}